import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export type ScoutToolState =
  | "input_streaming"
  | "input_available"
  | "output_available"
  | "output_error";

export type ScoutTextPart = {
  id: string;
  type: "text";
  text: string;
};

export type ScoutToolPart = {
  id: string;
  type: "tool";
  toolName: string;
  toolDisplayName?: string;
  toolCallId: string;
  toolState: ScoutToolState;
  toolInput?: unknown;
  toolOutput?: unknown;
  toolError?: string;
  toolDurationMs?: number;
};

export type ScoutMessagePart = ScoutTextPart | ScoutToolPart;

export type ScoutChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  parts?: ScoutMessagePart[];
  isStreaming?: boolean;
};

export type ScoutThreadSummary = {
  id: string;
  title: string;
};

const SEEDED_CONVERSATIONS: Record<string, ScoutChatMessage[]> = {
  "seed-tier-ii": [
    {
      id: "seed-tier-ii-m1",
      role: "user",
      content: "When is the Tier II report due this year?",
    },
    {
      id: "seed-tier-ii-m2",
      role: "assistant",
      content:
        "Tier II reports are due by March 1 each year, covering the previous calendar year's chemical inventory.",
    },
  ],
  "seed-waste": [
    {
      id: "seed-waste-m1",
      role: "user",
      content: "How do I schedule a hazardous waste pickup?",
    },
    {
      id: "seed-waste-m2",
      role: "assistant",
      content:
        "You can request a pickup from the Waste module — open a facility, go to Waste, and click Request Pickup.",
    },
    {
      id: "seed-waste-m3",
      role: "user",
      content: "Can I pick the vendor?",
    },
    {
      id: "seed-waste-m4",
      role: "assistant",
      content: "Yes — the vendor dropdown shows every transporter contracted for that facility.",
    },
  ],
  "seed-facility": [
    {
      id: "seed-facility-m1",
      role: "user",
      content: "What do I need to onboard a new facility?",
    },
    {
      id: "seed-facility-m2",
      role: "assistant",
      content:
        "You'll need the facility's address, primary contact, and an initial chemical inventory. I can walk you through each step.",
    },
  ],
};

export const SEEDED_THREAD_SUMMARIES: ScoutThreadSummary[] = [
  { id: "seed-tier-ii", title: "Tier II reporting deadline" },
  { id: "seed-waste", title: "Scheduling a hazardous waste pickup" },
  { id: "seed-facility", title: "Onboarding a new facility" },
];

function buildStubResponse(userPrompt: string): string {
  const preview = userPrompt.length > 120 ? `${userPrompt.slice(0, 120)}…` : userPrompt;
  return `Received the prompt "${preview}". Thinking and responding…`;
}

function buildStubToolParts(userPrompt: string): ScoutToolPart[] {
  const lower = userPrompt.toLowerCase();
  const parts: ScoutToolPart[] = [];

  if (lower.includes("single tool")) {
    parts.push({
      id: crypto.randomUUID(),
      type: "tool",
      toolName: "query_facilities",
      toolDisplayName: "Looking up facilities",
      toolCallId: `call_${crypto.randomUUID()}`,
      toolState: "output_available",
      toolInput: { tenantId: "demo-tenant", limit: 10 },
      toolOutput: { count: 62 },
      toolDurationMs: 142,
    });
    return parts;
  }

  if (lower.includes("facility") || lower.includes("facilities")) {
    parts.push({
      id: crypto.randomUUID(),
      type: "tool",
      toolName: "query_facilities",
      toolDisplayName: "Looking up facilities",
      toolCallId: `call_${crypto.randomUUID()}`,
      toolState: "output_available",
      toolInput: { tenantId: "demo-tenant", limit: 10 },
      toolOutput: { count: 62, durationMs: 142 },
      toolDurationMs: 142,
    });
  }

  if (lower.includes("chemical") || lower.includes("inventory")) {
    parts.push({
      id: crypto.randomUUID(),
      type: "tool",
      toolName: "search_chemicals",
      toolDisplayName: "Searching chemicals",
      toolCallId: `call_${crypto.randomUUID()}`,
      toolState: "output_available",
      toolInput: { query: "hazardous", threshold: 10000 },
      toolOutput: { rows: 23 },
      toolDurationMs: 310,
    });
  }

  if (parts.length === 0) {
    parts.push(
      {
        id: crypto.randomUUID(),
        type: "tool",
        toolName: "query_facilities",
        toolDisplayName: "Looking up facilities",
        toolCallId: `call_${crypto.randomUUID()}`,
        toolState: "output_available",
        toolInput: { status: "ACTIVE" },
        toolOutput: { count: 62 },
        toolDurationMs: 98,
      },
      {
        id: crypto.randomUUID(),
        type: "tool",
        toolName: "get_tier_ii_reports",
        toolDisplayName: "Fetching Tier II reports",
        toolCallId: `call_${crypto.randomUUID()}`,
        toolState: "output_available",
        toolInput: { year: 2025 },
        toolOutput: { rows: 12 },
        toolDurationMs: 221,
      },
      {
        id: crypto.randomUUID(),
        type: "tool",
        toolName: "summarize_findings",
        toolDisplayName: "Summarizing findings",
        toolCallId: `call_${crypto.randomUUID()}`,
        toolState: "output_available",
        toolInput: { topic: userPrompt.slice(0, 40) },
        toolOutput: { summary: "Pulled together context for response." },
        toolDurationMs: 57,
      }
    );
  }

  return parts;
}

function streamAssistantText(
  fullText: string,
  onChunk: (partial: string) => void,
  onDone: () => void
): () => void {
  let index = 0;
  let cancelled = false;
  const step = 2;
  const delayMs = 18;

  const tick = () => {
    if (cancelled) return;
    index = Math.min(fullText.length, index + step);
    onChunk(fullText.slice(0, index));
    if (index < fullText.length) {
      window.setTimeout(tick, delayMs);
    } else {
      onDone();
    }
  };

  tick();
  return () => {
    cancelled = true;
  };
}

export const useScoutChatWithMocks = (conversationId: string | undefined) => {
  const navigate = useNavigate();
  const [messagesById, setMessagesById] =
    useState<Record<string, ScoutChatMessage[]>>(SEEDED_CONVERSATIONS);
  const [isSending, setIsSending] = useState(false);
  const cancelStreamRef = useRef<(() => void) | null>(null);
  const streamingAssistantIdRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      cancelStreamRef.current?.();
    };
  }, []);

  const messages = conversationId ? (messagesById[conversationId] ?? []) : [];

  const updateMessage = useCallback(
    (convId: string, messageId: string, mutate: (m: ScoutChatMessage) => ScoutChatMessage) => {
      setMessagesById((prev) => ({
        ...prev,
        [convId]: (prev[convId] ?? []).map((m) => (m.id === messageId ? mutate(m) : m)),
      }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (text: string) => {
      cancelStreamRef.current?.();

      let targetId = conversationId;
      if (!targetId) {
        targetId = crypto.randomUUID();
        navigate(targetId, { replace: true });
      }
      const capturedId = targetId;

      if (streamingAssistantIdRef.current) {
        const previousAssistantId = streamingAssistantIdRef.current;
        streamingAssistantIdRef.current = null;
        updateMessage(capturedId, previousAssistantId, (m) => ({ ...m, isStreaming: false }));
      }

      const userMessage: ScoutChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: text,
      };
      const assistantId = crypto.randomUUID();
      streamingAssistantIdRef.current = assistantId;
      const stub = buildStubResponse(text);
      const finishedTools = buildStubToolParts(text);
      const pendingTools: ScoutToolPart[] = finishedTools.map((t) => ({
        ...t,
        toolState: "input_available",
        toolOutput: undefined,
        toolDurationMs: undefined,
      }));
      const textPartId = crypto.randomUUID();

      setMessagesById((prev) => ({
        ...prev,
        [capturedId]: [
          ...(prev[capturedId] ?? []),
          userMessage,
          {
            id: assistantId,
            role: "assistant",
            content: "",
            isStreaming: true,
            parts: [...pendingTools, { id: textPartId, type: "text", text: "" }],
          },
        ],
      }));
      setIsSending(true);

      const timers: number[] = [];
      let cancelled = false;

      const perToolDelayMs = 600;
      finishedTools.forEach((finished, i) => {
        const timer = window.setTimeout(
          () => {
            if (cancelled) return;
            updateMessage(capturedId, assistantId, (m) => ({
              ...m,
              parts: m.parts?.map((p) =>
                p.type === "tool" && p.id === finished.id ? finished : p
              ),
            }));
          },
          perToolDelayMs * (i + 1)
        );
        timers.push(timer);
      });

      const textStartDelayMs = perToolDelayMs * (finishedTools.length + 1);
      const startTextTimer = window.setTimeout(() => {
        if (cancelled) return;
        const cancelStream = streamAssistantText(
          stub,
          (partial) => {
            updateMessage(capturedId, assistantId, (m) => ({
              ...m,
              content: partial,
              parts: m.parts?.map((p) =>
                p.id === textPartId && p.type === "text" ? { ...p, text: partial } : p
              ),
              isStreaming: true,
            }));
          },
          () => {
            setIsSending(false);
            updateMessage(capturedId, assistantId, (m) => ({ ...m, isStreaming: false }));
            if (streamingAssistantIdRef.current === assistantId) {
              streamingAssistantIdRef.current = null;
            }
            cancelStreamRef.current = null;
          }
        );
        cancelStreamRef.current = () => {
          cancelled = true;
          cancelStream();
          setIsSending(false);
          updateMessage(capturedId, assistantId, (m) => ({ ...m, isStreaming: false }));
          if (streamingAssistantIdRef.current === assistantId) {
            streamingAssistantIdRef.current = null;
          }
          cancelStreamRef.current = null;
        };
      }, textStartDelayMs);
      timers.push(startTextTimer);

      cancelStreamRef.current = () => {
        cancelled = true;
        timers.forEach((t) => window.clearTimeout(t));
        setIsSending(false);
        updateMessage(capturedId, assistantId, (m) => ({ ...m, isStreaming: false }));
        if (streamingAssistantIdRef.current === assistantId) {
          streamingAssistantIdRef.current = null;
        }
        cancelStreamRef.current = null;
      };
    },
    [conversationId, navigate, updateMessage]
  );

  const abortMessage = useCallback(() => {
    cancelStreamRef.current?.();
  }, []);

  return {
    messages,
    isSending,
    handleSubmit,
    abortMessage,
  };
};
